/**
 * Deterministic transcendental functions built only from IEEE-754 basic operations
 * (+ - * / and Math.round), which are exactly specified in every JS engine.
 * Math.sin/cos/exp are implementation-defined and may differ between V8 and JavaScriptCore.
 * Math.sqrt is correctly rounded by the spec and is safe to use directly.
 */

export const PI = 3.141592653589793;
const HALF_PI = PI / 2;
const LN2 = 0.6931471805599453;

function sinPoly(r: number): number {
  const r2 = r * r;
  let term = r;
  let sum = r;
  for (let n = 1; n <= 9; n++) {
    term *= -r2 / (2 * n * (2 * n + 1));
    sum += term;
  }
  return sum;
}

function cosPoly(r: number): number {
  const r2 = r * r;
  let term = 1;
  let sum = 1;
  for (let n = 1; n <= 9; n++) {
    term *= -r2 / ((2 * n - 1) * (2 * n));
    sum += term;
  }
  return sum;
}

export function detSin(x: number): number {
  const q = Math.round(x / HALF_PI);
  const r = x - q * HALF_PI;
  switch (((q % 4) + 4) % 4) {
    case 0:
      return sinPoly(r);
    case 1:
      return cosPoly(r);
    case 2:
      return -sinPoly(r);
    default:
      return -cosPoly(r);
  }
}

export function detCos(x: number): number {
  return detSin(x + HALF_PI);
}

export function detExp(x: number): number {
  const k = Math.round(x / LN2);
  const r = x - k * LN2;
  let term = 1;
  let sum = 1;
  for (let n = 1; n <= 22; n++) {
    term *= r / n;
    sum += term;
  }
  let scale = 1;
  const factor = k >= 0 ? 2 : 0.5;
  for (let i = 0; i < Math.abs(k); i++) scale *= factor;
  return sum * scale;
}

export function degToRad(deg: number): number {
  return (deg * PI) / 180;
}
